import React, { useEffect, useRef, useState } from 'react';
import { SerializedStyles } from '@emotion/react';
import { gql, useQuery } from '@apollo/client';
import { handleError } from '@domain-group/fe-helper';
import { Button, LinkButton } from '@domain-group/fe-blueprint-ui/button';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExternalLinkIcon,
} from '@domain-group/fe-co-icon';
import type { VendorReview } from '../../generated/graphql';
import type { RecommendationPayload } from '../../@types/recommendations';
import ReviewCard from './review-card';
import * as styles from './reviews-list.style';

const RmaExternalLink = ({
  href,
  css,
  onClick,
}: {
  href: string;
  css?: SerializedStyles;
  onClick?: () => void;
}): JSX.Element => (
  <LinkButton
    css={css}
    appearance="link"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    icon={<ExternalLinkIcon />}
    iconPosition="right"
    onClick={onClick}
    onAuxClick={onClick}
  >
    View all reviews on RateMyAgent
  </LinkButton>
);

const PLATINUM_PAGE_SIZE = 50;
const DEFAULT_PAGE_SIZE = 5;
const COLLAPSED_COUNT = 5;

export const AGENT_PROFILE_REVIEWS_QUERY = gql`
  query getAgentProfileReviews(
    $contactId: Int!
    $pageSize: Int
    $pageNumber: Int
  ) {
    contactByContactId(contactId: $contactId) {
      id
      recommendations(pageSize: $pageSize, pageNumber: $pageNumber) {
        agentProfileUrl
        reviews {
          title
          description
          reviewedOn
          reviewType
          starRating
          propertyType
          bedrooms
          suburb
          agentReviewTrackingPixelUrl
        }
      }
    }
  }
`;

type AgentProfileReviewsQueryResult = {
  contactByContactId?: {
    recommendations?: {
      agentProfileUrl?: string | null;
      reviews?: Array<VendorReview | null> | null;
    } | null;
  } | null;
};

export type ReviewsListProps = {
  contactId: number;
  isPlatinum: boolean;
  onUnavailable: () => void;
  onPageRendered?: (data: RecommendationPayload) => HTMLImageElement;
  onVendorRecoViewAll?: () => void;
  onVendorRecoShowMore?: () => void;
};

const ReviewsList = ({
  contactId,
  isPlatinum,
  onUnavailable,
  onPageRendered,
  onVendorRecoViewAll,
  onVendorRecoShowMore,
}: ReviewsListProps): JSX.Element | null => {
  const [showAll, setShowAll] = useState(false);
  const pageSize = isPlatinum ? PLATINUM_PAGE_SIZE : DEFAULT_PAGE_SIZE;
  const hasFiredPageRendered = useRef(false);

  const { data, loading, error } = useQuery<AgentProfileReviewsQueryResult>(
    AGENT_PROFILE_REVIEWS_QUERY,
    { variables: { contactId, pageSize, pageNumber: 1 } },
  );

  const reviews = (
    data?.contactByContactId?.recommendations?.reviews ?? []
  ).filter(
    (
      review,
    ): review is VendorReview & {
      title: string;
      description: string;
      reviewedOn: string;
    } => Boolean(review?.title && review.description && review.reviewedOn),
  );
  const agentProfileUrl =
    data?.contactByContactId?.recommendations?.agentProfileUrl;

  useEffect(() => {
    if (!loading && error) {
      handleError(error);
    }
    if (!loading && (error || reviews.length === 0)) {
      onUnavailable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, reviews.length]);

  useEffect(() => {
    if (
      !hasFiredPageRendered.current &&
      !loading &&
      !error &&
      reviews.length > 0 &&
      onPageRendered
    ) {
      hasFiredPageRendered.current = true;
      onPageRendered(data?.contactByContactId as RecommendationPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, reviews.length]);

  if (loading || error || reviews.length === 0) {
    return null;
  }

  const hasMoreThanCollapsedCount = reviews.length > COLLAPSED_COUNT;
  // "Clicking on the expander will reveal up to 50 reviews in the list" -
  // cap client-side too, not just via the fetch's pageSize.
  const displayedReviews = isPlatinum
    ? reviews.slice(0, showAll ? PLATINUM_PAGE_SIZE : COLLAPSED_COUNT)
    : reviews;

  return (
    <div>
      <ul css={styles.list}>
        {displayedReviews.map((review) => (
          <li key={`${review.title}-${review.reviewedOn}`}>
            <ReviewCard
              title={review.title ?? ''}
              description={review.description ?? ''}
              reviewedOn={review.reviewedOn ?? ''}
              starRating={review.starRating ?? 0}
              reviewType={review.reviewType}
              bedrooms={review.bedrooms}
              propertyType={review.propertyType}
              suburb={review.suburb}
            />
          </li>
        ))}
      </ul>
      {isPlatinum && (
        <div css={styles.ctas}>
          {hasMoreThanCollapsedCount && (
            <Button
              type="button"
              appearance="link"
              aria-expanded={showAll}
              icon={showAll ? <ChevronUpIcon /> : <ChevronDownIcon />}
              iconPosition="right"
              onClick={() => {
                if (!showAll) {
                  onVendorRecoShowMore?.();
                }
                setShowAll(!showAll);
              }}
            >
              {showAll ? 'Read less reviews' : 'Read all reviews'}
            </Button>
          )}
          {(!hasMoreThanCollapsedCount || showAll) && agentProfileUrl && (
            <RmaExternalLink
              href={agentProfileUrl}
              onClick={onVendorRecoViewAll}
            />
          )}
        </div>
      )}
      {/* Gold/Silver: fixed list, no expander - only the RMA link shows. */}
      {!isPlatinum && agentProfileUrl && (
        <RmaExternalLink
          href={agentProfileUrl}
          css={styles.expanderButton}
          onClick={onVendorRecoViewAll}
        />
      )}
    </div>
  );
};

export default ReviewsList;
